import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Bike, ChevronRight, KeyRound, Loader2, Search, ShieldCheck, UserPlus, Users } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../shared/Toast';
import { API_BASE_URL } from '../config/api';
import EmployeeForm, { PosTerminalOption, SaveFailure } from './EmployeeForm';
import {
  PERMISSION_ROLES, RoleOption, StoreEmployee, currentPinLength, fullName, isHqRole, isLegacyRole,
  listedEmployees, pinLengthFor, pinMismatch, sortEmployees,
} from './employeeModel';

/**
 * «Υπάλληλοι» στη διαχείριση καταστήματος (απόφαση ιδιοκτήτη 12/10/2026): όλοι οι υπάλληλοι (και οι
 * ανενεργοί), ένα παράθυρο για νέο/αλλαγή με στοιχεία, ρόλο, PIN, «Ενεργός» και δικαιώματα του ατόμου.
 * Εκτός οι διανομείς: δεν συνδέονται, ζουν στο Delivery → «Διανομείς» (delivery/DriversSection).
 * Αντικαθιστά τη λίστα της StoreAdminDashboard και την οθόνη «Δικαιώματα Προσωπικού».
 *
 * API: GET/POST/PUT /api/stores/:id/employees (routes/storeAdmin.js), GET /api/roles?for=employee_form,
 * GET /api/stores/:id/pos/terminals (μόνο με kiosk).
 */

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('xentrix_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

type LoadState = 'loading' | 'ready' | 'error' | 'forbidden';
type Editing = { employee: StoreEmployee | null } | null;

const chip = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium';

// «μαρια» βρίσκει τη «Μαρία»: χωρίς τόνους και πεζά.
const searchKey = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase('el');

const EmployeesPanel: React.FC = () => {
  const { store, employee: me } = useAuth();
  const { showToast } = useToast();
  const { t } = useTranslation(['employees', 'common']);
  const titleId = useId();
  const [employees, setEmployees] = useState<StoreEmployee[]>([]);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [terminals, setTerminals] = useState<PosTerminalOption[]>([]);
  const [load, setLoad] = useState<LoadState>('loading');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Editing>(null);
  const [highlight, setHighlight] = useState<number | null>(null);

  const storeId = store?.id;
  const kioskEnabled = !!store?.kiosk_enabled;
  // Χρήστης οργανισμού μέσα στο κατάστημα: το id του δεν είναι υπαλλήλου — ποτέ «εσύ».
  const myEmployeeId = me && !(me as { org_context?: boolean }).org_context ? me.id : null;

  const loadEmployees = useCallback(async (): Promise<StoreEmployee[] | null> => {
    const res = await fetch(`${API_BASE_URL}/api/stores/${storeId}/employees`, { headers: authHeaders() });
    if (res.status === 401 || res.status === 403) { setLoad('forbidden'); return null; }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = (await res.json()) as StoreEmployee[];
    setEmployees(Array.isArray(data) ? data : []);
    return data;
  }, [storeId]);

  const loadAll = useCallback(async () => {
    if (!storeId) return;
    setLoad('loading');
    try {
      const [list, roleRes] = await Promise.all([
        loadEmployees(),
        fetch(`${API_BASE_URL}/api/roles?for=employee_form`, { headers: authHeaders() }),
      ]);
      if (!list) return;
      if (!roleRes.ok) throw new Error(`HTTP ${roleRes.status}`);
      setRoles((await roleRes.json()) as RoleOption[]);
      if (kioskEnabled) {
        // Χωρίς τερματικά η φόρμα δουλεύει (kiosk «Χωρίς τερματικό»).
        const termRes = await fetch(`${API_BASE_URL}/api/stores/${storeId}/pos/terminals`, { headers: authHeaders() }).catch(() => null);
        if (termRes?.ok) {
          const list2 = (await termRes.json()) as PosTerminalOption[];
          setTerminals(Array.isArray(list2) ? list2.filter(tm => tm.is_active !== false) : []);
        }
      }
      setLoad('ready');
    } catch {
      setLoad('error');
    }
  }, [storeId, kioskEnabled, loadEmployees]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Η γραμμή που μόλις αποθηκεύτηκε φαίνεται για λίγο.
  useEffect(() => {
    if (highlight == null) return undefined;
    const timer = setTimeout(() => setHighlight(null), 4000);
    return () => clearTimeout(timer);
  }, [highlight]);

  const submit = async (body: Record<string, unknown>, target: StoreEmployee | null): Promise<StoreEmployee | null> => {
    // Χωρίς απάντηση (δίκτυο) το fetch πετά χωρίς status → «Δεν υπάρχει σύνδεση».
    const res = await fetch(`${API_BASE_URL}/api/stores/${storeId}/employees${target ? `/${target.id}` : ''}`, {
      method: target ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      const failure: SaveFailure = Object.assign(new Error(`HTTP ${res.status}`), { status: res.status, data });
      throw failure;
    }
    return (data && data.employee) || null;
  };

  const saved = async (result: StoreEmployee | null, name: string, wasCreate: boolean) => {
    setEditing(null);
    showToast('success', wasCreate ? t('employees:toast.created', { name }) : t('employees:toast.saved', { name }));
    if (result?.id) setHighlight(result.id);
    // Ξαναδιαβάζει όλη τη λίστα: ό,τι έγραψε ο server, όχι ό,τι νομίζει η φόρμα.
    try { await loadEmployees(); } catch { /* μένει η παλιά λίστα· το μήνυμα επιτυχίας ισχύει */ }
  };

  const roleLabel = (e: StoreEmployee) => t(`employees:roles.${e.role_name}`, { defaultValue: e.role_display_name || e.role_name });

  // Χωρίς τους διανομείς — ο server τους στέλνει ακόμη (Ζ, analytics), η οθόνη όχι.
  const listed = useMemo(() => listedEmployees(employees), [employees]);
  const driverCount = employees.length - listed.length;

  const visible = useMemo(() => {
    const q = searchKey(search.trim());
    const sorted = sortEmployees(listed);
    if (!q) return sorted;
    return sorted.filter(e => [fullName(e), roleLabel(e), e.phone || ''].some(s => searchKey(s).includes(q)));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- το roleLabel αλλάζει μόνο με τη γλώσσα
  }, [listed, search, t]);

  const activeCount = listed.filter(e => e.is_active).length;

  const pinChip = (e: StoreEmployee) => {
    if (!e.has_pin) {
      return <span className={`${chip} bg-amber-500/15 text-amber-800 dark:text-amber-300`}><AlertTriangle className="w-3 h-3" />{t('employees:pinState.missing')}</span>;
    }
    if (pinMismatch(e)) {
      return (
        <span className={`${chip} bg-amber-500/15 text-amber-800 dark:text-amber-300`}>
          <AlertTriangle className="w-3 h-3" />{t('employees:pinState.mismatch', { actual: currentPinLength(e), expected: pinLengthFor(e.role_name) })}
        </span>
      );
    }
    return <span className={`${chip} bg-surface-3 text-t2`}><KeyRound className="w-3 h-3" />{t('employees:pinState.set', { digits: currentPinLength(e) })}</span>;
  };

  const row = (e: StoreEmployee) => {
    const name = fullName(e) || '—';
    const readOnly = isHqRole(e.role_name);
    const content = (
      <>
        <span className="w-9 h-9 rounded-full bg-surface-3 flex items-center justify-center text-xs font-bold text-t1 shrink-0" aria-hidden="true">
          {(e.first_name || '?').charAt(0)}{(e.last_name || '').charAt(0)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-sm font-semibold text-t1 truncate">{name}</span>
            {e.id === myEmployeeId && <span className="text-xs text-t3">({t('employees:you')})</span>}
            {e.phone && <span className="text-xs text-t3 tabular-nums">{e.phone}</span>}
          </span>
          <span className="mt-1 flex flex-wrap items-center gap-1.5">
            <span className={`${chip} bg-surface-3 text-t1 font-semibold`}>{roleLabel(e)}</span>
            {isLegacyRole(e.role_name) && (
              <span className={`${chip} bg-amber-500/15 text-amber-800 dark:text-amber-300`}>
                <AlertTriangle className="w-3 h-3" />{t('employees:legacyRole')}
              </span>
            )}
            {pinChip(e)}
            <span className={`${chip} ${e.is_active ? 'bg-green-500/15 text-green-700 dark:text-green-400' : 'bg-surface-3 text-t3'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${e.is_active ? 'bg-green-600 dark:bg-green-400' : 'bg-t4'}`} aria-hidden="true" />
              {e.is_active ? t('employees:status.active') : t('employees:status.inactive')}
            </span>
            {e.has_custom_permissions && PERMISSION_ROLES.includes(e.role_name) && (
              <span className={`${chip} bg-surface-3 text-t2`}><ShieldCheck className="w-3 h-3" />{t('employees:customPermissions')}</span>
            )}
            {readOnly && <span className="text-[11px] text-t3">{t('employees:hqAccount')}</span>}
          </span>
        </span>
        {!readOnly && <ChevronRight className="w-4 h-4 text-t4 shrink-0" aria-hidden="true" />}
      </>
    );
    const cls = `w-full flex items-center gap-3 px-4 py-3 text-left bg-surface-2 ${e.is_active ? '' : 'opacity-75'} ${highlight === e.id ? 'ring-2 ring-inset ring-brand' : ''}`;
    return (
      <li key={e.id} data-testid={`employee-row-${e.id}`}>
        {readOnly ? (
          <div className={cls}>{content}</div>
        ) : (
          <button type="button" className={`${cls} hover:bg-surface-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand`}
            aria-label={t('employees:editAria', { name })} onClick={() => { setHighlight(null); setEditing({ employee: e }); }}>
            {content}
          </button>
        )}
      </li>
    );
  };

  return (
    <section className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line overflow-hidden" aria-labelledby={titleId}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 border-b border-line">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-brand-soft rounded-xl flex items-center justify-center shrink-0">
            <Users className="h-5 w-5 text-brand" />
          </div>
          <div className="min-w-0">
            <h2 id={titleId} className="text-lg font-bold text-t1">{t('employees:title')}</h2>
            <p className="text-xs text-t3">{t('employees:subtitle')}</p>
          </div>
        </div>
        <button type="button" disabled={load !== 'ready'} onClick={() => { setHighlight(null); setEditing({ employee: null }); }}
          className="inline-flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-xl text-sm font-semibold bg-brand text-brand-fg hover:brightness-110 active:scale-[0.98] transition disabled:opacity-50 disabled:cursor-not-allowed">
          <UserPlus className="h-4 w-4" />
          {t('employees:add')}
        </button>
      </div>

      <div className="p-5 space-y-4">
        {load === 'loading' && (
          <div className="flex items-center justify-center gap-2 py-12 text-t3" role="status">
            <Loader2 className="w-5 h-5 animate-spin" />{t('employees:loading')}
          </div>
        )}
        {(load === 'error' || load === 'forbidden') && (
          <div className="py-10 text-center space-y-3" role="alert">
            <p className="text-sm text-t2">{load === 'forbidden' ? t('employees:forbidden') : t('employees:loadError')}</p>
            {load === 'error' && (
              <button type="button" onClick={loadAll}
                className="min-h-[44px] px-4 rounded-xl text-sm font-medium border border-line-strong bg-surface-2 text-t1 hover:bg-surface-3">
                {t('employees:retry')}
              </button>
            )}
          </div>
        )}
        {load === 'ready' && (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-t4 h-4 w-4" aria-hidden="true" />
                <input type="search" value={search} onChange={e => setSearch(e.target.value)}
                  placeholder={t('employees:searchPlaceholder')} aria-label={t('employees:searchPlaceholder')}
                  className="w-full min-h-[44px] pl-10 pr-4 bg-surface-2 border border-line-strong rounded-xl text-t1 placeholder-t4 text-sm focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand" />
              </div>
              <p className="text-xs text-t3 sm:text-right shrink-0">
                {t('employees:total', { count: listed.length })} · {t('employees:activeCount', { count: activeCount })}
              </p>
            </div>

            {(driverCount > 0 || store?.delivery_self) && (
              <p className="flex items-center gap-1.5 text-xs text-t3" data-testid="drivers-hint">
                <Bike className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />{t('employees:driversHint')}
              </p>
            )}

            {listed.length === 0 ? (
              <p className="text-center py-12 text-sm text-t3">{t('employees:empty')}</p>
            ) : visible.length === 0 ? (
              <p className="text-center py-12 text-sm text-t3">{t('employees:noResults')}</p>
            ) : (
              <ul className="rounded-xl border border-line overflow-hidden divide-y divide-line">
                {visible.map(row)}
              </ul>
            )}
          </>
        )}
      </div>

      {editing && (
        <EmployeeForm
          key={editing.employee?.id ?? 'new'}
          employee={editing.employee}
          roles={roles}
          store={store}
          terminals={terminals}
          isSelf={!!editing.employee && editing.employee.id === myEmployeeId}
          onSubmit={body => submit(body, editing.employee)}
          onSaved={(result, name) => saved(result, name, !editing.employee)}
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
};

export default EmployeesPanel;
